import { useMemo } from 'react'
import { StylesConfig, OptionProps, GroupBase } from 'react-select'

import { CSSObject, useTheme } from '@emotion/react'

import { getInputBlockPadding } from '../../../mixins'

import { SelectProps } from './Select'
import { SelectBaseOption } from './types'

const SELECT_MENU_MAX_HEIGHT = '200px'

function getOptionHoverBackgroundColor<
  Option extends SelectBaseOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(state: OptionProps<Option, IsMulti, Group>) {
  if (state.isSelected) {
    return 'blue.75'
  }

  if (state.isDisabled) {
    return 'white.100'
  }

  return 'blue.100'
}

function getOptionColor<
  Option extends SelectBaseOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(state: OptionProps<Option, IsMulti, Group>) {
  if (state.isSelected) {
    return 'blue.100'
  }

  if (state.isDisabled) {
    return 'navy.25'
  }

  return 'navy.100'
}

function getControlBorderColor<
  Option extends SelectBaseOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(isDisabled: boolean, selectProps: SelectProps<Option, IsMulti, Group>) {
  if (isDisabled) {
    return 'navy.10'
  }

  if (selectProps.invalid) {
    return 'red.100'
  }

  if (selectProps.warning) {
    return 'orange.100'
  }

  return 'navy.25'
}

export const useCustomStyles = <
  Option extends SelectBaseOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  hasSubOptions,
  menuInPortal,
  menuMaxHeight = SELECT_MENU_MAX_HEIGHT,
}: {
  hasSubOptions: boolean
  menuInPortal?: boolean
  menuMaxHeight?: CSSObject['maxHeight']
}): StylesConfig<Option, IsMulti, Group> => {
  const theme = useTheme()
  const {
    ui: { typography, spacings, colors },
  } = theme

  const paddingBlock = getInputBlockPadding({
    theme,
    type: 'value',
  }) as string

  return useMemo(
    () => ({
      option: (provided, { isDisabled, isSelected, ...rest }) => {
        return {
          ...provided,
          display: 'flex',
          alignItems: 'center',
          fontSize: typography.fontSizes.sm,
          lineHeight: typography.lineHeights.sm,
          backgroundColor: colors['white.100'],
          color: colors[getOptionColor({ isDisabled, isSelected, ...rest })],
          minHeight: spacings.xxl,
          paddingBlock,
          paddingInline: spacings.md,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor:
              colors[
                getOptionHoverBackgroundColor({
                  isDisabled,
                  isSelected,
                  ...rest,
                })
              ],
            color: colors[isDisabled && !isSelected ? 'navy.25' : 'white.100'],
          },
          ...(rest.data.__isNew__
            ? {
                borderTop: '1px solid',
                borderTopColor: colors['navy.25'],
                // way to get rid of double borders when there is only "Create new option" option available
                // rest.options provides list of all options, not the currently visible ones, so we
                // would have to do filtering here, which can be expensive
                marginTop: '-1px',
              }
            : {}),
        }
      },
      noOptionsMessage: (provided) => ({
        ...provided,
        color: colors['navy.25'],
        minHeight: spacings.xxl,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: typography.fontSizes.sm,
        lineHeight: typography.lineHeights.sm,
      }),
      loadingMessage: (provided) => ({
        ...provided,
        color: colors['navy.25'],
        minHeight: spacings.xxl,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: typography.fontSizes.sm,
        lineHeight: typography.lineHeights.sm,
      }),
      control: (provided, { isDisabled, menuIsOpen, selectProps }) => ({
        ...provided,
        borderRadius: spacings.sm,
        height: spacings.xxl,
        boxShadow: 'none',
        borderColor: colors[getControlBorderColor(isDisabled, selectProps)],
        backgroundColor: colors[isDisabled ? 'navy.10' : 'white.100'],
        color: colors[isDisabled ? 'navy.25' : 'navy.100'],
        padding: `0 ${spacings.md}`,
        cursor: menuIsOpen ? 'default' : 'pointer',

        '&:hover': {
          borderColor: colors[menuIsOpen ? 'navy.25' : 'blue.100'],
          ...(!menuIsOpen
            ? { color: colors[menuIsOpen ? 'navy.25' : 'blue.100'] }
            : {}),
        },
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: 0,
      }),
      container: (provided) => ({
        ...provided,
        width: '100%',
      }),
      singleValue: (provided, { isDisabled }) => ({
        ...provided,
        margin: 0,
        transition: 'opacity 300ms',
        color: colors[isDisabled ? 'navy.25' : 'navy.100'],
        fontSize: typography.fontSizes.sm,
        lineHeight: typography.lineHeights.sm,
        padding: 0,
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      input: (provided) => ({
        ...provided,
        color: colors['navy.100'],
        margin: 0,
        padding: 0,
      }),
      placeholder: (provided) => ({
        ...provided,
        color: colors['navy.25'],
        fontWeight: typography.fontWeights.regular,
        fontSize: typography.fontSizes.sm,
        lineHeight: typography.lineHeights.sm,
        marginLeft: 0,
      }),
      menu: (provided) => ({
        ...provided,
        marginTop: spacings.sm,
        marginBottom: spacings.sm,
        borderRadius: spacings.sm,
        boxShadow: 'none',
      }),
      group: (provided) => ({
        ...provided,
        padding: `${spacings.sm} 0 0 0`,
      }),
      groupHeading: (provided) => ({
        ...provided,
        textTransform: 'none',
        paddingInline: spacings.md,
        paddingBlock: spacings.sm,
        color: colors['navy.25'],
        fontSize: typography.fontSizes.xxs,
        lineHeight: typography.lineHeights.xxs,
        fontWeight: typography.fontWeights.semiBold,
        marginBottom: 0,
      }),
      menuList: (provided) => ({
        border: `1px solid ${colors['navy.25']}`,
        borderRadius: spacings.sm,
        ...(hasSubOptions
          ? {
              maxHeight: menuMaxHeight,
              overflowX: 'hidden',
              overflowY: 'auto',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }
          : {
              ...provided,
              maxHeight: menuMaxHeight,
              overflowY: 'scroll',
              padding: 0,
            }),
      }),
      menuPortal: (provided) =>
        menuInPortal
          ? {
              ...provided,
              fontFamily: typography.fontFamilies.default,
              zIndex: 9999,
            }
          : provided,
    }),
    [
      typography.fontSizes.sm,
      typography.fontSizes.xxs,
      typography.lineHeights.sm,
      typography.lineHeights.xxs,
      typography.fontWeights.regular,
      typography.fontWeights.semiBold,
      typography.fontFamilies.default,
      colors,
      spacings.xxl,
      spacings.md,
      spacings.sm,
      paddingBlock,
      hasSubOptions,
      menuMaxHeight,
      menuInPortal,
    ],
  )
}
